import { clamp, getRandom, random } from 'helpers';
import * as PIXI from 'pixi.js';
import * as setup from 'setup';
import Entity from '../Entity';

export default class Food extends Entity {

    bounds: PIXI.Rectangle;
    boundsCellNames: string[];
    props: EntityBaseProps & FoodProps;

    calories = 0;
    caloriesRemaining = 0;
    engaged = false;
    found = false;
    remaining = 1;

    constructor(gameState: GameState, type?: FoodType, props?: EntityBaseProps & FoodProps, state?: any) {
        // Get type
        if (!type) {
            const types: FoodType[] = ['acorn', 'apple', 'applepie', 'bread1', 'bread2', 'carrot', 'cherry', 'corn', 'donuts', 'mushroom', 'redgrapes', 'watermelon'];
            type = getRandom(types);
        }

        // Get props
        props = {
            idleTexture: `food/food-${type}.png`,
            ...props,
        };

        // Get random position
        if (props.x === undefined && props.y === undefined) {
            while(props.x === undefined || props.y === undefined || gameState.gridMatrix[props.y][props.x]) {
                props.x = random(0, setup.map.cells.wide - 1, true);
                props.y = random(0, setup.map.cells.wide - 1, true);
            }
        }

        // Call super
        super(gameState, 'food', type, {}, props, state);

        // Initialize
        this.props = props;
        this.active = this.type !== 'cheese';
        this.calories = props?.calories || (50 * random(0, 4, true) + 100);
        this.caloriesRemaining = this.calories;
        this.gameState = gameState;
        this.zIndex = 2;

        // Get bounds
        this.bounds = new PIXI.Rectangle(0, 0, this.size, this.size);
        this.bounds.x = clamp(this.cell.x - Math.floor(this.size / 2), 0, setup.map.cells.wide - 1);
        this.bounds.y = clamp(this.cell.y - Math.floor(this.size / 2), 0, setup.map.cells.wide - 1);
        this.bounds.width = clamp(this.bounds.width, 0, setup.map.cells.wide - this.bounds.x - 1);
        this.bounds.height = clamp(this.bounds.height, 0, setup.map.cells.wide - this.bounds.y - 1);

        // Get bounds cell names
        this.boundsCellNames = [];
        for (let x = this.bounds.left; x < this.bounds.right; x++) {
            for (let y = this.bounds.top; y < this.bounds.bottom; y++) {
                this.boundsCellNames.push(`${x},${y}`);
            }
        }

        // Check found if some fog on bounds is revealed
        const revealedCells = this.boundsCellNames.filter(cellName => !this.gameState.fog[cellName]);
        this.checkFound(revealedCells);
    }

    getSnapshotState = () => {
        return {
            calories: this.calories,
            caloriesRemaining: this.caloriesRemaining,
            engaged: this.engaged,
            found: this.found,
            remaining: this.remaining,
        }
    }

    checkFound = (cellNames: string[]) => {
        if (this.caloriesRemaining && !this.found && cellNames.some(cellName => this.boundsCellNames.includes(cellName))) {
            this.found = true;
            this.gameState.revealFog(this.cell, this.size / 2);
        }
    }

    setCalories = (amount: number) => {
        this.caloriesRemaining = amount;
        this.calories = amount / this.remaining;
    }

    grabCalories = (amount: number) => {
        // Check found
        if (!this.found) {
            return 0;
        }

        this.engaged = true;
        return this.subtractCalories(amount);
    }

    subtractCalories = (amount: number) => {
        // Subtract max amount from calories remaining
        const subtractableAmount = Math.min(this.caloriesRemaining, amount);
        this.caloriesRemaining -= subtractableAmount;
        this.remaining = this.caloriesRemaining / this.calories;

        // Reduce size
        const scale = 0.5 * this.caloriesRemaining / this.calories + 0.5;
        this.scale.set(scale);
        this.size = Math.ceil(scale * (this.props.size || 1));

        // Destroy if consumed
        if (this.caloriesRemaining <= 0) {
            this.gameState.removeEntity(null, null, this);
        }

        return subtractableAmount;
    }

}